import { useRef, useState } from 'react';

import * as connectionsService from '../service';
import {
  IGetAuthorizedConnectionResponseData,
  WooCommerceSetupInfo,
} from '../types';
import { useToast } from '../../../contexts/ToastContext/ToastContext';
import { useConnectionTypes } from '../useConnectionTypes';

interface ICreateAdverityConnectionRequestData {
  connectionTypeId: number;
  domain?: string;
  appsflyerApiToken?: string;
  onAuthorized: () => void;
  customerId?: string;
  openAiApiKey?: string;
  woocommerceInfo?: WooCommerceSetupInfo;
}

export const useConnection = () => {
  const [isAccountLinking, setIsAccountLinking] = useState(false);
  const [isAccountUnlinking, setIsAccountUnlinking] = useState(false);
  const [currentLinkedID, setCurrentLinkedID] = useState('');
  const { showToast } = useToast();
  const authorizeWithTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const { getConnectionTypes } = useConnectionTypes();

  const getLinkedAccountId = async ({
    connectionTypeId,
    domain,
    customerId,
    appsflyerApiToken,
    openAiApiKey,
    woocommerceInfo,
  }: {
    connectionTypeId: number;
    domain?: string;
    customerId?: string;
    appsflyerApiToken?: string;
    openAiApiKey?: string;
    woocommerceInfo?: WooCommerceSetupInfo;
  }): Promise<string> => {
    let linkedAccountId = '';
    try {
      linkedAccountId = await connectionsService.createConnection({
        connectionTypeId,
        domainName: domain,
        customerId,
        appsflyerApiToken,
        openAiApiKey,
        woocommerceInfo,
      });
    } catch (error) {
      setIsAccountLinking(false);
      const connectionName = getConnectionTypes.data?.find(
        c => c.id === connectionTypeId
      )?.name;
      showToast(
        `Error occurred while linking ${connectionName || 'connection'}`,
        'error'
      );
    }
    setCurrentLinkedID(linkedAccountId);
    return linkedAccountId;
  };

  const authorizeAdConnection = async (
    linkedAccountID: string,
    handleOnAuthorized: () => void,
    openNewTab?: boolean
  ): Promise<void> => {
    try {
      const authorizeResponse: IGetAuthorizedConnectionResponseData =
        await connectionsService.authorizeConnection(linkedAccountID);
      const isAuthorized = authorizeResponse.is_authorized;
      if (openNewTab && authorizeResponse.is_oauth) {
        const win = window.open(authorizeResponse.url, '_blank');
        win?.focus();
      }
      if (isAuthorized || !authorizeResponse.is_oauth) {
        if (authorizeWithTimeout.current) {
          clearTimeout(authorizeWithTimeout.current);
        }
        handleOnAuthorized();
        setIsAccountLinking(false);
      } else {
        authorizeWithTimeout.current = setTimeout(() => {
          authorizeAdConnection(linkedAccountID, handleOnAuthorized);
        }, 3000);
      }
    } catch (error) {
      if (authorizeWithTimeout.current) {
        clearTimeout(authorizeWithTimeout.current);
      }
      setIsAccountLinking(false);
      throw error;
    }
  };

  const createAdConnection = async ({
    connectionTypeId,
    onAuthorized,
    domain,
    customerId,
    appsflyerApiToken,
    openAiApiKey,
    woocommerceInfo,
  }: ICreateAdverityConnectionRequestData): Promise<void> => {
    setIsAccountLinking(true);
    const linkedAccountID = await getLinkedAccountId({
      connectionTypeId,
      domain,
      customerId,
      appsflyerApiToken,
      openAiApiKey,
      woocommerceInfo,
    });
    if (linkedAccountID) {
      await authorizeAdConnection(linkedAccountID, onAuthorized, true);
    }
  };

  const abort = async (customerId?: string): Promise<void> => {
    if (authorizeWithTimeout.current) {
      clearTimeout(authorizeWithTimeout.current);
    }
    try {
      if (currentLinkedID) {
        await connectionsService.cancelConnection({
          linkedAccountId: currentLinkedID,
          customerId,
        });
      }
      setIsAccountLinking(false);
    } catch (error) {
      setIsAccountLinking(false);
    }
  };

  const unlinkAdAccount = async ({
    linkedAccountId,
    customerId,
  }: {
    linkedAccountId: string;
    customerId?: string;
  }): Promise<void> => {
    setIsAccountUnlinking(true);
    try {
      await connectionsService.unlinkAccount({ customerId, linkedAccountId });
    } finally {
      setIsAccountUnlinking(false);
    }
  };

  return {
    isAccountLinking,
    isAccountUnlinking,
    createAdConnection,
    abort,
    unlinkAdAccount,
  };
};
